import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getBlogCategories, getBlogPosts } from '../api/client';
import { BlogCategory, BlogPostInfo } from '../api/types';
import me from '../assets/images/me-1.jpeg';
import { useLanguage } from '../i18n/LanguageContext';

const LATEST_POSTS = 3;

function AboutPanel() {
  const { t, lang } = useLanguage();
  const [posts, setPosts] = useState<BlogPostInfo[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);

  useEffect(() => {
    Promise.all([getBlogPosts(), getBlogCategories()])
      .then(([postsRes, categoriesRes]) => {
        setPosts(postsRes);
        setCategories(categoriesRes);
      })
      .catch(() => setPosts([]));
  }, []);

  const latest = useMemo(
    () =>
      [...posts]
        .sort((a, b) => new Date(b.published_at ?? 0).getTime() - new Date(a.published_at ?? 0).getTime())
        .slice(0, LATEST_POSTS),
    [posts]
  );

  const categoryLabel = (slug: string) => {
    const c = categories.find((x) => x.slug === slug);
    return c ? (lang === 'en' ? c.name_en : c.name_tr) : slug;
  };

  return (
    <section id="about" className="panel rv-panel">
      <div className="panel-inner about">
        <div className="about-cols">
          <div className="about-intro">
            <div className="eyebrow rv">{t.about.eyebrow}</div>
            <div className="about-id rv rv-1">
              <img src={me} alt="Furkan Turan" className="about-photo" />
              <div className="about-id-text">
                <span className="about-name">Furkan Turan</span>
                <span className="about-sub">{t.about.sub}</span>
              </div>
            </div>
            <p className="about-lead rv rv-2">
              {t.about.p1} <span className="dim">{t.about.p2}</span>
            </p>
            <div className="about-skills rv rv-3">
              {t.about.skills.map(([k, v]) => (
                <div key={k}>
                  <span className="about-skill-key">{k}</span>
                  <span>{v}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="about-exp">
            <div className="eyebrow rv about-exp-title">{t.about.experience}</div>
            {t.about.jobs.map(([when, role, org]) => (
              <div key={when + role} className="job rv">
                <span className="job-when">{when}</span>
                <div>
                  <div className="job-role">{role}</div>
                  <div className="job-org">{org}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {latest.length > 0 && (
          <div id="writing" className="writing">
            <div className="writing-head">
              <div className="eyebrow rv">{t.writing.eyebrow}</div>
              <Link to="/blog" className="writing-all rv">
                {t.writing.allPosts}
              </Link>
            </div>
            <div className="writing-grid">
              {latest.map((post, i) => (
                <Link key={post.id} to={`/blog/${post.slug}`} className={`writing-card rv rv-${i + 1}`}>
                  <span className="writing-kind">
                    {categoryLabel(post.category)}
                    {post.published_at &&
                      ` · ${new Date(post.published_at).toLocaleDateString(lang === 'en' ? 'en-US' : 'tr-TR', {
                        month: 'short',
                        year: 'numeric',
                      })}`}
                  </span>
                  <span className="writing-title">{lang === 'en' ? post.title_en : post.title_tr || post.title_en}</span>
                  <span className="writing-summary">{lang === 'en' ? post.summary_en : post.summary_tr || post.summary_en}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default AboutPanel;
